/**
 * 智能问答状态管理
 * Vuex 模块 - 管理聊天状态、会话列表、消息历史
 */

import { getAppParameters } from '@/api/qa';
import { getUserId } from '@/api/qa';
import { createUserMessage, createAssistantPlaceholder, finalizeMessage, markMessageError } from '@/utils/qa/messageBuffer';
import { sendStreamMessage, stopStreamResponse } from '@/utils/qa/eventStream';

const STORAGE_KEY_CONVERSATIONS = 'qa_conversations';

export default {
  namespaced: true,

  state: {
    // 用户信息
    userId: '',
    userAvatar: '',

    // 当前会话
    currentConversationId: '',
    currentTaskId: '',

    // 消息列表
    messages: [],

    // 会话列表
    conversations: [],

    // 加载状态
    isLoading: false,
    isStreaming: false,

    // 应用参数
    openingStatement: '我可以帮您解答政务服务相关问题，如办事流程、材料准备、政策咨询等。',
    suggestedQuestions: [
      '如何办理营业执照？',
      '身份证丢失如何补办？',
      '社保缴费如何查询？',
      '不动产登记需要哪些材料？'
    ],

    // 错误状态
    error: null
  },

  mutations: {
    SET_USER_INFO(state, { userId, avatar }) {
      state.userId = userId || getUserId();
      state.userAvatar = avatar || localStorage.getItem('rtc_headimgurl') || '';
    },

    SET_LOADING(state, val) {
      state.isLoading = val;
    },

    SET_STREAMING(state, val) {
      state.isStreaming = val;
    },

    SET_TASK_ID(state, taskId) {
      state.currentTaskId = taskId;
    },

    SET_CONVERSATION_ID(state, id) {
      state.currentConversationId = id;
    },

    SET_APP_PARAMETERS(state, { openingStatement, suggestedQuestions }) {
      if (openingStatement) state.openingStatement = openingStatement;
      if (suggestedQuestions?.length) state.suggestedQuestions = suggestedQuestions;
    },

    ADD_MESSAGE(state, message) {
      state.messages.push(message);
    },

    UPDATE_MESSAGE(state, { index, updater }) {
      if (state.messages[index]) {
        Object.assign(state.messages[index], updater);
      }
    },

    SET_MESSAGES(state, messages) {
      state.messages = messages || [];
    },

    CLEAR_MESSAGES(state) {
      state.messages = [];
      state.currentConversationId = '';
      state.currentTaskId = '';
    },

    SET_CONVERSATIONS(state, list) {
      state.conversations = list || [];
    },

    ADD_CONVERSATION(state, conv) {
      state.conversations.unshift(conv);
    },

    UPDATE_CONVERSATION(state, { id, updater }) {
      const idx = state.conversations.findIndex(c => c.id === id);
      if (idx > -1) {
        Object.assign(state.conversations[idx], updater);
      }
    },

    REMOVE_CONVERSATION(state, id) {
      state.conversations = state.conversations.filter(c => c.id !== id);
    },

    SET_ERROR(state, error) {
      state.error = error;
    },

    CLEAR_ERROR(state) {
      state.error = null;
    },

    LOAD_CONVERSATIONS_FROM_STORAGE(state) {
      try {
        const saved = localStorage.getItem(STORAGE_KEY_CONVERSATIONS);
        if (saved) {
          state.conversations = JSON.parse(saved);
        }
      } catch (e) {
        console.log('加载会话记录失败');
      }
    }
  },

  actions: {
    // 初始化用户
    initUser({ commit }) {
      commit('SET_USER_INFO', {});
    },

    // 加载应用参数
    async loadAppParameters({ commit }) {
      try {
        const res = await getAppParameters();
        if (res) {
          commit('SET_APP_PARAMETERS', {
            openingStatement: res.opening_statement,
            suggestedQuestions: res.suggested_questions
          });
        }
      } catch (e) {
        console.log('加载应用参数失败，使用默认值');
      }
    },

    // 发送消息（流式）
    async sendMessage({ commit, state, dispatch }, { text, signal }) {
      if (!text || state.isStreaming) return;

      // 添加用户消息
      const userMsg = createUserMessage(text);
      commit('ADD_MESSAGE', userMsg);
      commit('SET_LOADING', true);
      commit('CLEAR_ERROR');

      // 添加助手消息占位
      const assistantIndex = state.messages.length;
      const placeholder = createAssistantPlaceholder();
      commit('ADD_MESSAGE', placeholder);

      try {
        const baseURL = window.config.qaApi;
        const apiKey = window.config.qaApiKey;

        await sendStreamMessage(baseURL, apiKey, {
          query: text,
          user: state.userId,
          conversation_id: state.currentConversationId || undefined
        }, {
          onMessage: (chunk) => {
            commit('SET_LOADING', false);
            commit('SET_STREAMING', true);
            const msg = state.messages[assistantIndex];
            if (msg) {
              msg.content += chunk;
              // 触发响应式更新
              commit('UPDATE_MESSAGE', { index: assistantIndex, updater: { content: msg.content } });
            }
          },
          onEnd: (conversationId) => {
            commit('SET_STREAMING', false);
            if (conversationId) {
              commit('SET_CONVERSATION_ID', conversationId);
            }
            const msg = state.messages[assistantIndex];
            if (msg) {
              commit('UPDATE_MESSAGE', {
                index: assistantIndex,
                updater: {
                  isStreaming: false,
                  time: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
                  status: 'done'
                }
              });
            }
          },
          onError: (error) => {
            commit('SET_STREAMING', false);
            commit('UPDATE_MESSAGE', {
              index: assistantIndex,
              updater: {
                isStreaming: false,
                content: '抱歉，服务暂时不可用，请稍后再试。',
                time: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
                status: 'error'
              }
            });
            commit('SET_ERROR', error);
          },
          onTaskId: (taskId) => {
            commit('SET_TASK_ID', taskId);
          }
        }, signal);

      } catch (error) {
        commit('SET_STREAMING', false);
        if (error.name === 'AbortError') {
          commit('UPDATE_MESSAGE', {
            index: assistantIndex,
            updater: {
              isStreaming: false,
              content: state.messages[assistantIndex]?.content + '\n\n[已停止]',
              time: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
              status: 'stopped'
            }
          });
        } else {
          commit('UPDATE_MESSAGE', {
            index: assistantIndex,
            updater: {
              isStreaming: false,
              content: '抱歉，服务暂时不可用，请稍后再试。',
              time: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
              status: 'error'
            }
          });
          commit('SET_ERROR', error.message);
        }
      } finally {
        commit('SET_LOADING', false);
        commit('SET_STREAMING', false);
      }
    },

    // 开始新对话
    startNewConversation({ commit }) {
      commit('CLEAR_MESSAGES');
    },

    // 停止响应
    async stopResponse({ state }) {
      if (!state.currentTaskId) return;
      try {
        const baseURL = window.config.qaApi;
        const apiKey = window.config.qaApiKey;
        await stopStreamResponse(baseURL, apiKey, state.currentTaskId, state.userId);
      } catch (e) {
        console.error('停止响应失败:', e);
      }
    }
  },

  getters: {
    hasMessages: state => state.messages.length > 0,
    currentConversation: state => state.conversations.find(c => c.id === state.currentConversationId),
    isBusy: state => state.isLoading || state.isStreaming
  }
};
